
import { useState, ChangeEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { User } from "@supabase/supabase-js";
import { Opportunity } from "@/types/database-extensions";

interface UseOpportunityFormProps {
  currentOpportunity: Partial<Opportunity>;
  setCurrentOpportunity: (opportunity: Partial<Opportunity>) => void;
  setIsDialogOpen: (isOpen: boolean) => void;
  user: User | null;
}

export const useOpportunityForm = ({ 
  currentOpportunity,
  setCurrentOpportunity,
  setIsDialogOpen,
  user
}: UseOpportunityFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // Fix: Change the way we're updating the state to avoid the type error
    setCurrentOpportunity({
      ...currentOpportunity,
      [name]: value
    });
  };

  const handleCreate = () => {
    setCurrentOpportunity({
      title: "",
      description: "",
      organization: "",
      deadline: "",
      application_url: "",
      location: "",
      type: "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to manage opportunities",
          variant: "destructive",
        });
        return;
      }

      if (!currentOpportunity.title || !currentOpportunity.description) {
        toast({
          title: "Missing fields",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      if (currentOpportunity.id) {
        // Check if user has admin role
        const { data: isAdmin } = await supabase
          .from("user_roles")
          .select("*")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .maybeSingle();
          
        // Check if user owns the opportunity
        const { data: opportunity } = await supabase
          .from("opportunities")
          .select("user_id")
          .eq("id", currentOpportunity.id)
          .single();
        
        // Only allow update if user is admin or the creator of the opportunity
        if (!(isAdmin || (opportunity && opportunity.user_id === user.id))) {
          throw new Error("You don't have permission to update this opportunity");
        }
        
        // Update existing
        const { error } = await supabase
          .from("opportunities")
          .update({
            title: currentOpportunity.title,
            description: currentOpportunity.description,
            organization: currentOpportunity.organization,
            deadline: currentOpportunity.deadline,
            application_url: currentOpportunity.application_url,
            location: currentOpportunity.location,
            type: currentOpportunity.type,
            updated_at: new Date().toISOString(),
          })
          .eq("id", currentOpportunity.id);

        if (error) throw error;

        toast({
          title: "Updated",
          description: "Opportunity has been updated",
        });
      } else {
        // Create new
        const { error } = await supabase.from("opportunities").insert({
          title: currentOpportunity.title,
          description: currentOpportunity.description,
          organization: currentOpportunity.organization,
          deadline: currentOpportunity.deadline,
          application_url: currentOpportunity.application_url,
          location: currentOpportunity.location,
          type: currentOpportunity.type,
          user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        if (error) throw error;

        toast({
          title: "Success",
          description: "New opportunity has been created",
        });
      }

      setIsDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleCreate,
    handleSubmit,
    handleInputChange,
    isSubmitting
  };
};
